import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService } from '../services/api';
import OTPInput from '../components/common/OTPInput';
import Button from '../components/common/Button';
import { useAuth } from '../context/AuthContext';

const Login = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { login } = useAuth();
    const [phoneNumber, setPhoneNumber] = useState('');
    const [otp, setOtp] = useState('');
    const [showOtpInput, setShowOtpInput] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [otpError, setOtpError] = useState('');
    const [resendDisabled, setResendDisabled] = useState(false);
    const [resendTimer, setResendTimer] = useState(0);

    useEffect(() => {
        // If redirected from a protected route, show the error message
        if (location.state?.from) {
            setError('Please login to access this page');
        }
    }, [location]);

    const startResendTimer = () => {
        setResendDisabled(true);
        setResendTimer(30);
        const timer = setInterval(() => {
            setResendTimer((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    setResendDisabled(false);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    // Phone number validation
    const validatePhoneNumber = (phone) => {
        return /^\d{10}$/.test(phone);
    };

    const handleSendOtp = async () => {
        if (!phoneNumber || !validatePhoneNumber(phoneNumber)) {
            setError('Please enter a valid 10-digit phone number');
            return;
        }

        setLoading(true);
        setError('');
        try {
            const response = await authService.checkUser(phoneNumber);
            if (response.success) {
                setShowOtpInput(true);
                startResendTimer();
            } else {
                // If user not found, redirect to signup
                navigate('/signup', { state: { phoneNumber } });
            }
        } catch (err) {
            setError(err.message || 'Failed to send OTP');
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOtp = async () => {
        if (!otp || otp.length !== 6) {
            setOtpError('Please enter a valid 6-digit OTP');
            return;
        }

        setLoading(true);
        setError('');
        setOtpError('');
        try {
            const response = await authService.verifyOtp(phoneNumber, otp);
            console.log('Verify OTP Response:', response); // Debug log
            
            if (response.success && response.data) {
                // Store the token and update auth state
                login(response.data);
                
                // Navigate to the intended page or dashboard
                const from = location.state?.from?.pathname || '/dashboard';
                console.log('Navigating to:', from); // Debug log
                navigate(from, { replace: true });
            } else {
                setOtpError(response.message || 'Invalid OTP');
            }
        } catch (err) {
            console.error('OTP Verification Error:', err); // Debug log
            setOtpError(err.message || 'Failed to verify OTP');
        } finally {
            setLoading(false);
        }
    };

    const handleResendOtp = async () => {
        if (resendDisabled) return;

        setLoading(true);
        setError('');
        try {
            const response = await authService.resendOtp(phoneNumber);
            if (response.success) {
                startResendTimer();
            } else {
                setError(response.message || 'Failed to resend OTP');
            }
        } catch (err) {
            setError(err.message || 'Failed to resend OTP');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-sm sm:max-w-md w-full space-y-6 sm:space-y-8 bg-white p-6 sm:p-8 rounded-lg shadow-md">
                <div className="text-center">
                    <h1 className="text-3xl font-bold text-gray-800">Welcome to HairQure Admin</h1>
                </div>
                <div>
                    <h2 className="mt-2 sm:mt-6 text-center text-2xl sm:text-3xl font-extrabold text-gray-900">
                        {showOtpInput ? 'Enter OTP' : 'Login to your account'}
                    </h2>
                    <p className="mt-2 text-center text-sm text-gray-600">
                        {showOtpInput
                            ? 'Enter the OTP sent to your phone'
                            : 'Enter your phone number to continue'}
                    </p>
                </div>

                <div className="mt-6 sm:mt-8 space-y-4 sm:space-y-6">
                    {!showOtpInput ? (
                        <div>
                            <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-1">
                                Phone Number
                            </label>
                            <input
                                id="phoneNumber"
                                type="tel"
                                required
                                className="input"
                                placeholder="Phone Number"
                                value={phoneNumber}
                                onChange={(e) => {
                                    // Only allow digits
                                    const value = e.target.value.replace(/\D/g, '');
                                    setPhoneNumber(value);
                                    if (value.length > 10) setError('Phone number cannot exceed 10 digits');
                                    else setError('');
                                }}
                                maxLength={10}
                            />
                            <Button
                                onClick={handleSendOtp}
                                loading={loading}
                                variant="primary"
                                className="w-full mt-4"
                            >
                                Send OTP
                            </Button>
                        </div>
                    ) : (
                        <div>
                            <OTPInput
                                length={6}
                                value={otp}
                                onChange={setOtp}
                                error={otpError}
                            />
                            <div className="flex items-center justify-between mt-2">
                                <button
                                    type="button"
                                    onClick={handleResendOtp}
                                    disabled={resendDisabled}
                                    className={`text-sm font-medium ${
                                        resendDisabled
                                            ? 'text-gray-400 cursor-not-allowed'
                                            : 'text-indigo-600 hover:text-indigo-500'
                                    }`}
                                >
                                    {resendDisabled
                                        ? `Resend OTP in ${resendTimer}s`
                                        : 'Resend OTP'}
                                </button>
                            </div>
                            <Button
                                onClick={handleVerifyOtp}
                                loading={loading}
                                variant="primary"
                                className="w-full mt-4"
                            >
                                Verify OTP
                            </Button>
                        </div>
                    )}
                    {error && (
                        <div className="rounded-md bg-red-50 p-3 sm:p-4">
                            <p className="text-sm text-red-600 text-center">{error}</p>
                        </div>
                    )}
                    
                    {/* Navigation to Signup */}
                    <div className="text-center">
                        <p className="text-sm text-gray-600">
                            Don't have an account?{' '}
                            <button
                                type="button"
                                onClick={() => navigate('/signup')}
                                className="font-medium text-indigo-600 hover:text-indigo-500"
                            >
                                Sign up here
                            </button>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login; 